import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MdClose, MdAdd, MdCheck } from "react-icons/md";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { thunks as groupSvc } from "src/thunks/group";
import { aptApi } from "src/api/apt";
import { enrichApts } from "src/utils/enrich-apts";
import { AptLinkButtons } from "src/components/apt-link-buttons";
import { AptChartCard } from "src/pages/dashboard/home/apt-chart-card";
import { AptSummaryCard } from "src/components/apt-summary-card";

/** 평형 default 우선순위 — 30평대 → 20 → 40 → 10 → 50+ */
const PYEONG_RANGES = [
  [30, 39], [20, 29], [40, 49], [10, 19], [50, 9999],
];

/** 응답에 따라 supplyPyeong(Double) 또는 pyeong(정수) 둘 중 하나 사용 */
const getPy = (p) => p?.supplyPyeong ?? p?.pyeong;

/** 단지 평형 목록에서 default 선택할 평형을 결정 */
const pickDefaultPyeong = (pyeongs) => {
  if (!Array.isArray(pyeongs) || pyeongs.length === 0) return null;
  for (const [lo, hi] of PYEONG_RANGES) {
    const matched = pyeongs
      .filter((p) => getPy(p) != null && getPy(p) >= lo && getPy(p) <= hi)
      .sort((a, b) => getPy(a) - getPy(b));
    if (matched.length > 0) return matched[0];
  }
  // 우선순위 안 맞으면 가장 작은 평형
  return [...pyeongs].sort((a, b) => (getPy(a) ?? 9999) - (getPy(b) ?? 9999))[0];
};

/**
 * 관심아파트 등록 다이얼로그
 * - 데스크톱: 좌측 그룹 리스트 + 우측 컨텐츠
 * - 모바일: 상단 그룹 드롭다운 + 컨텐츠 풀폭
 *
 * @param {string} aptId        - 단지 ID (필수)
 * @param {string} pyeongId     - 평형 ID. 없으면 단지의 평형 목록을 fetch해 dropdown 노출.
 * @param {string} aptName
 * @param {number} supplyPyeong - pyeongId가 있을 때 표시용
 */
export const FavoriteAptDialog = ({
  open,
  onClose,
  aptId,
  pyeongId: propPyeongId,
  aptName,
  supplyPyeong: propSupplyPyeong,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const aptGroups = useSelector((state) => state.group.aptGroups || []);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [groupApts, setGroupApts] = useState([]);
  const [aptsLoading, setAptsLoading] = useState(false);
  // pyeongId가 prop으로 안 오면 단지 평형 목록 fetch + dropdown으로 선택
  const [aptPyeongs, setAptPyeongs] = useState([]);
  const [pickedPyeongId, setPickedPyeongId] = useState(propPyeongId ?? null);
  // 선택된 pyeongId의 상세 정보(시세/매물/실거래) — 헤더 아래에 요약 표시
  const [selectedApt, setSelectedApt] = useState(null);

  // 실제 등록/제거에 사용할 pyeongId — prop 우선, 없으면 사용자가 dropdown으로 선택한 것
  const pyeongId = propPyeongId ?? pickedPyeongId;
  // 표시용 supplyPyeong
  const supplyPyeong = propSupplyPyeong
    ?? aptPyeongs.find((p) => p.pyeongId === pickedPyeongId)?.supplyPyeong
    ?? null;

  // 다이얼로그 열릴 때 그룹 목록 fetch (서버가 빈 그룹이면 "나의그룹" 자동 생성)
  useEffect(() => {
    if (open) dispatch(groupSvc.getAptGroups());
  }, [open, dispatch]);

  useEffect(() => {
    if (open && aptGroups.length > 0 && selectedGroupId == null) {
      setSelectedGroupId(aptGroups[0].id);
    }
  }, [open, aptGroups, selectedGroupId]);

  useEffect(() => {
    if (!open) {
      setSelectedGroupId(null);
      setNewGroupName("");
      setGroupApts([]);
      setAptPyeongs([]);
      setPickedPyeongId(propPyeongId ?? null);
      setSelectedApt(null);
    }
  }, [open, propPyeongId]);

  // pyeongId가 prop으로 안 오면 → 단지명 검색(searchSupplyPyeongs)으로 SPyeong 단위 평형 목록 fetch.
  // (getPyeongsCInfo는 EPyeong 단위라 그룹 selectedApts(SPyeong)와 ID 체계가 다름)
  // searchSupplyPyeongs는 SPYEONG 단위 응답이고 pyeongId가 SPyeong ID, pyeong은 정수 평형.
  useEffect(() => {
    if (!open || !aptId || propPyeongId || !aptName) return;
    let cancelled = false;
    aptApi
      .searchSupplyPyeongs(aptName, { page: 0, rowsPerPage: 100 })
      .then((res) => {
        if (cancelled) return;
        // 같은 이름 다른 단지가 있을 수 있어 aptId로 필터 + 평형 정수 단위로 중복 제거.
        // searchSupplyPyeongs 응답엔 pyeong(정수)만 있고 supplyPyeong이 없을 수 있어
        // enrichApts의 extractRealPrices가 supplyPyeong으로 매칭하므로 명시적으로 보장.
        const all = (res?.content || []).filter((a) => a.aptId === aptId);
        const seen = new Set();
        const list = [];
        for (const a of all) {
          const sp = Math.round(getPy(a) ?? 0);
          if (sp === 0 || seen.has(sp)) continue;
          seen.add(sp);
          list.push({ ...a, supplyPyeong: a.supplyPyeong ?? sp });
        }
        list.sort((a, b) => (getPy(a) ?? 9999) - (getPy(b) ?? 9999));
        setAptPyeongs(list);
        if (list.length > 0) {
          const def = pickDefaultPyeong(list);
          setPickedPyeongId(def?.pyeongId ?? list[0].pyeongId);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, aptId, propPyeongId, aptName]);

  // 선택된 pyeongId의 상세(시세/매물/실거래) — aptPyeongs에 있으면 그대로 사용, 없으면 fetch
  useEffect(() => {
    if (!open || !pyeongId) {
      setSelectedApt(null);
      return;
    }
    let cancelled = false;
    // 1) aptPyeongs(이미 fetch한 SPyeong 목록)에서 직접 찾기 — 시세/매물 정보 이미 포함
    const found = aptPyeongs.find((p) => p.pyeongId === pyeongId);
    if (found) {
      // 즉시 raw 데이터로 표시 후 enrichApts로 실거래/신고가 보강
      setSelectedApt(found);
      enrichApts([found])
        .then((enriched) => { if (!cancelled && enriched?.[0]) setSelectedApt(enriched[0]); })
        .catch(() => {});
      return () => { cancelled = true; };
    }
    // 2) aptPyeongs에 없으면 (propPyeongId가 직접 전달된 경우 등) — byIds로 fetch
    aptApi
      .getSPyeongPriceDtoByIds([pyeongId], { page: 0, rowsPerPage: 1, sortBy: null, sortDir: null })
      .then(async (res) => {
        if (cancelled) return;
        const apts = res?.content || [];
        if (apts.length === 0) {
          setSelectedApt(null);
          return;
        }
        setSelectedApt(apts[0]);
        try {
          const enriched = await enrichApts(apts);
          if (!cancelled && enriched?.[0]) setSelectedApt(enriched[0]);
        } catch { /* enrich 실패해도 raw 그대로 */ }
      })
      .catch(() => { if (!cancelled) setSelectedApt(null); });
    return () => { cancelled = true; };
  }, [open, pyeongId, aptPyeongs]);

  const selectedGroup = useMemo(
    () => aptGroups.find((g) => g.id === selectedGroupId) ?? null,
    [aptGroups, selectedGroupId],
  );

  const isInSelectedGroup = useMemo(() => {
    if (!selectedGroup || !pyeongId) return false;
    return (selectedGroup.selectedApts || []).includes(pyeongId);
  }, [selectedGroup, pyeongId]);

  // 선택된 그룹의 아파트 기본 정보 + 실거래/신고가/시세변동 enrich
  useEffect(() => {
    if (!selectedGroup) {
      setGroupApts([]);
      return;
    }
    const ids = selectedGroup.selectedApts || [];
    if (ids.length === 0) {
      setGroupApts([]);
      return;
    }
    let cancelled = false;
    setAptsLoading(true);

    const run = async () => {
      try {
        const priceRes = await aptApi.getSPyeongPriceDtoByIds(ids, {
          page: 0, rowsPerPage: 100, sortBy: null, sortDir: null,
        });
        const apts = priceRes?.content || [];
        if (apts.length === 0) {
          if (!cancelled) setGroupApts([]);
          return;
        }
        const enriched = await enrichApts(apts);
        if (!cancelled) setGroupApts(enriched);
      } catch {
        if (!cancelled) setGroupApts([]);
      } finally {
        if (!cancelled) setAptsLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [selectedGroup]);

  const handleAdd = useCallback(async (e) => {
    e?.stopPropagation();
    if (!selectedGroup || !pyeongId) return;
    const merged = [...new Set([...(selectedGroup.selectedApts || []), pyeongId])];
    await dispatch(groupSvc.updateAptGroup({ ...selectedGroup, selectedApts: merged }));
    toast.success(`'${selectedGroup.name}'에 추가됨`);
  }, [dispatch, selectedGroup, pyeongId]);

  const handleRemove = useCallback(async (e) => {
    e?.stopPropagation();
    if (!selectedGroup || !pyeongId) return;
    const filtered = (selectedGroup.selectedApts || []).filter((id) => id !== pyeongId);
    await dispatch(groupSvc.updateAptGroup({ ...selectedGroup, selectedApts: filtered }));
    toast.success(`'${selectedGroup.name}'에서 제거됨`);
  }, [dispatch, selectedGroup, pyeongId]);

  const handleCreateGroup = useCallback(async (e) => {
    e?.stopPropagation();
    const name = newGroupName.trim();
    if (!name) return;
    await dispatch(groupSvc.createAptGroup({
      name,
      desc: "",
      selectedApts: pyeongId ? [pyeongId] : [],
    }));
    toast.success(`'${name}' 그룹이 생성됨`);
    setNewGroupName("");
  }, [dispatch, newGroupName, pyeongId]);

  const handleSelectGroup = useCallback((e, gid) => {
    e?.stopPropagation?.();
    setSelectedGroupId(gid);
  }, []);

  const handleDialogClose = useCallback((e, reason) => {
    e?.stopPropagation?.();
    onClose?.(e, reason);
  }, [onClose]);

  const stopProp = (e) => e.stopPropagation();

  // ─── 좌측 그룹 리스트 (데스크톱) ─────────────────────────
  const groupListPanel = (
    <Box sx={{ width: 240, borderRight: 1, borderColor: "divider", display: "flex", flexDirection: "column" }}>
      <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 700, color: "text.secondary" }}>
        그룹
      </Typography>
      <Divider />
      <List dense sx={{ flex: 1, overflowY: "auto", py: 0 }}>
        {aptGroups.map((g, idx) => {
          const isSelected = g.id === selectedGroupId;
          const isContains = (g.selectedApts || []).includes(pyeongId);
          return (
            <ListItemButton
              key={g.id}
              selected={isSelected}
              onClick={(e) => handleSelectGroup(e, g.id)}
              sx={{ py: 0.8 }}
            >
              <ListItemText
                primary={
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                    {/* 그룹명 ellipsis + 최신 라벨 한 줄 유지 */}
                    <Typography
                      component="span"
                      noWrap
                      sx={{ flex: "1 1 auto", minWidth: 0, fontSize: 13, fontWeight: 600 }}
                    >
                      {g.name}
                    </Typography>
                    {isContains && <MdCheck size={14} color="#FFB400" style={{ flexShrink: 0 }} />}
                    {idx === 0 && (
                      <Typography variant="caption" sx={{ color: "primary.main", fontSize: 10, whiteSpace: "nowrap", flexShrink: 0 }}>· 최신</Typography>
                    )}
                  </Stack>
                }
                secondary={`${(g.selectedApts || []).length}개 단지`}
                primaryTypographyProps={{ component: "div" }}
                secondaryTypographyProps={{ fontSize: 11 }}
              />
            </ListItemButton>
          );
        })}
        {aptGroups.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: "block" }}>
            그룹 불러오는 중...
          </Typography>
        )}
      </List>
      <Divider />
      <Box sx={{ p: 1, display: "flex", gap: 0.5, alignItems: "center" }}>
        <TextField
          size="small"
          placeholder="새 그룹명"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") handleCreateGroup(e); }}
          onClick={stopProp}
          sx={{
            flex: 1,
            "& .MuiInputBase-root": { fontSize: 12 },
            "& .MuiInputBase-input": { py: 0.75, height: "auto" },
          }}
        />
        <IconButton size="small" onClick={handleCreateGroup} disabled={!newGroupName.trim()} sx={{ p: 0.5 }}>
          <MdAdd />
        </IconButton>
      </Box>
    </Box>
  );

  // ─── 모바일: 그룹 dropdown + 새 그룹 입력 ───────────────
  // - Select 메뉴 폭 = 트리거 폭 (화면 밖으로 나가지 않게 anchor 기준 정렬)
  // - MenuItem은 화면이 좁으면 wrap되도록 whiteSpace normal
  // - 새 그룹 생성: + 버튼이 왼쪽, TextField가 오른쪽
  const mobileGroupSelect = (
    <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider", flexShrink: 0, minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", flexShrink: 0 }}>
          그룹
        </Typography>
        <Select
          size="small"
          value={selectedGroupId ?? ""}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          onClick={stopProp}
          sx={{
            flex: 1,
            minWidth: 0,
            height: 32,
            fontSize: 13,
            // 닫힌 트리거의 그룹명이 길 때 ellipsis 처리
            "& .MuiSelect-select": {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
          // 닫힌 상태에서는 그룹명만 ellipsis로 표시 (메뉴 항목은 wrap 유지)
          renderValue={(value) => {
            const g = aptGroups.find((x) => x.id === value);
            return g ? g.name : "";
          }}
          MenuProps={{
            onClick: stopProp,
            PaperProps: {
              sx: {
                maxWidth: { xs: "calc(100vw - 32px)", md: 320 },
              },
            },
          }}
        >
          {aptGroups.map((g, idx) => {
            const isContains = (g.selectedApts || []).includes(pyeongId);
            return (
              <MenuItem
                key={g.id}
                value={g.id}
                onClick={stopProp}
                sx={{ fontSize: 13, whiteSpace: "normal", py: 0.8 }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexWrap: "wrap", minWidth: 0 }}>
                  <span style={{ wordBreak: "break-all" }}>{g.name}</span>
                  {isContains && <MdCheck size={14} color="#FFB400" />}
                  {idx === 0 && (
                    <Typography variant="caption" sx={{ color: "primary.main", fontSize: 10, whiteSpace: "nowrap", flexShrink: 0 }}>· 최신</Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, ml: 0.5 }}>
                    ({(g.selectedApts || []).length})
                  </Typography>
                </Stack>
              </MenuItem>
            );
          })}
        </Select>
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1, minWidth: 0 }}>
        <IconButton size="small" onClick={handleCreateGroup} disabled={!newGroupName.trim()} sx={{ p: 0.5, flexShrink: 0 }}>
          <MdAdd />
        </IconButton>
        <TextField
          size="small"
          placeholder="새 그룹명"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") handleCreateGroup(e); }}
          onClick={stopProp}
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiInputBase-root": { fontSize: 12 },
            "& .MuiInputBase-input": { py: 0.75, height: "auto" },
          }}
        />
      </Stack>
    </Box>
  );

  // ─── 우측(또는 모바일 본문): 선택된 아파트 + 그룹 정보 + 카드 리스트 ───
  const contentPanel = (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
      {/* 선택된 아파트 헤더 — 고정, 스크롤 X. pyeongId가 prop으로 안 오면 평형 dropdown 표시. */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary">선택된 아파트</Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ flexWrap: "wrap" }}
        >
          {!propPyeongId && aptPyeongs.length > 0 && (
            <Select
              size="small"
              value={pickedPyeongId ?? ""}
              onChange={(e) => setPickedPyeongId(e.target.value)}
              onClick={stopProp}
              MenuProps={{ onClick: stopProp }}
              sx={{
                height: 30,
                fontSize: 13,
                minWidth: 80,
                flexShrink: 0,
                "& .MuiSelect-select": { py: 0.5 },
              }}
            >
              {[...aptPyeongs]
                .sort((a, b) => (getPy(a) ?? 9999) - (getPy(b) ?? 9999))
                .map((p) => (
                  <MenuItem key={p.pyeongId} value={p.pyeongId} sx={{ fontSize: 13 }} onClick={stopProp}>
                    {Math.round(getPy(p))}평
                  </MenuItem>
                ))}
            </Select>
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: 14, md: 16 } }}>
            {aptName ?? "-"}{propPyeongId && supplyPyeong ? ` (${supplyPyeong}평)` : ""}
          </Typography>
          {aptId && <AptLinkButtons aptId={aptId} pyeongId={pyeongId} iconSize={14} showStar={false} />}
        </Stack>
        {/* 선택된 평형의 요약 정보 — AptSummaryCard와 동일 포맷.
            hideTitle: 헤더에 이미 이름/링크가 있으므로 카드 첫 줄(이름+링크)은 숨기고 지역명만 표시. */}
        {selectedApt && (
          <Box sx={{ mt: 1 }}>
            <AptSummaryCard apt={selectedApt} fullWidth hideTitle />
          </Box>
        )}
      </Box>
      {selectedGroup ? (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            spacing={{ xs: 1, sm: 0 }}
            sx={{ px: 2, py: 1.5, flexShrink: 0 }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">선택된 아파트그룹</Typography>
              <Typography variant="body2">
                <span style={{ fontWeight: 700 }}>{selectedGroup.name}</span>
                {isInSelectedGroup ? " 에 등록되어 있음 ✓" : " 에 미등록"}
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              {isInSelectedGroup ? (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={handleRemove}
                  sx={{ fontSize: 12, fontWeight: 700, py: 0.4, px: 1.5 }}
                >
                  그룹에서 제거
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAdd}
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    py: 0.4,
                    px: 1.5,
                    bgcolor: "#1976D2",
                    color: "#fff",
                    "&:hover": { bgcolor: "#1565C0" },
                  }}
                >
                  이 그룹에 추가
                </Button>
              )}
            </Box>
          </Stack>
          <Divider />
          <Box sx={{ px: 1.5, py: 1, flex: 1, minHeight: 0, overflowY: "auto" }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              이 그룹의 아파트 ({(selectedGroup.selectedApts || []).length})
            </Typography>
            {aptsLoading ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", py: 2 }}>
                불러오는 중...
              </Typography>
            ) : groupApts.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", py: 2 }}>
                그룹이 비어있습니다
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                {groupApts.map((apt) => (
                  <AptChartCard
                    key={apt.pyeongId}
                    apt={apt}
                    showChart={false}
                    fullWidth
                    showStar={false}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </>
      ) : (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.secondary">
            그룹을 선택하세요
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      onClick={stopProp}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5, px: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>관심아파트</Typography>
        <IconButton size="small" onClick={handleDialogClose}><MdClose /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent
        sx={{
          p: 0,
          height: { xs: "100%", md: 560 },
          minHeight: 0,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          overflow: "hidden",  // 자식의 overflow auto만 스크롤되도록
        }}
        onClick={stopProp}
      >
        {isMobile ? mobileGroupSelect : groupListPanel}
        {contentPanel}
      </DialogContent>
    </Dialog>
  );
};
